/**
 * Volume LRU bookkeeping for the labeling viewer grid.
 *
 * The grid switches cases IN PLACE (no remount), so it must evict the previous
 * study's GPU volumes itself or WebGL contexts/textures leak until the browser
 * force-loses contexts ("Too many active WebGL contexts" → panes blank until
 * refresh).
 *
 * The LRU MUST store FULL volume ids (`cornerstoneStreamingImageVolume:<study>_<series>`).
 * The previous implementation stored bare series UIDs and rebuilt the evict id
 * with the *current* studyId — so after a case switch it tried to dispose
 * `<studyB>_<seriesA>`, an id that was never cached (`disposeAndRemoveVolume`
 * returned false), and the real `<studyA>_<seriesA>` volume + its
 * vtkOpenGLTexture leaked forever. Keeping full ids means the evicted id always
 * carries the study it was cached under.
 */

export const VOLUME_ID_PREFIX = "cornerstoneStreamingImageVolume:";

/** Full streaming-image volume id for a (study, series) pair. */
export function volumeIdFor(studyId: string, seriesId: string): string {
  return `${VOLUME_ID_PREFIX}${studyId}_${seriesId}`;
}

/** Strip the prefix back to `<studyId>_<seriesId>` (the base used to build the
 *  matching segmentation id `<studyId>_<seriesId>_segmentation`). */
export function volumeBaseFromId(volumeId: string): string {
  return volumeId.startsWith(VOLUME_ID_PREFIX) ? volumeId.slice(VOLUME_ID_PREFIX.length) : volumeId;
}

/**
 * Of the cache's resident volume ids, those belonging to a study OTHER than
 * `currentStudyId` — i.e. the ones to purge on a case switch.
 *
 * The count-LRU only bounds the ≤`cap` volumes the grid binds to its slots, but
 * the grid stays mounted across case switches so volumes from every visited
 * study accumulate in the cache untracked (observed 47 volumes across 14 studies
 * pegging `maxCacheSize` → `CACHE_SIZE_EXCEEDED` + WebGL context loss). Purging
 * foreign-study volumes on each switch bounds the cache to the current study.
 *
 * Only ids matching the streaming-volume scheme are considered; the studyId is
 * the segment before the first `_` (DICOM UIDs are dotted, never contain `_`).
 * Non-matching cache keys (derived/segmentation/other) are left untouched.
 */
export function foreignStudyVolumeIds(
  residentVolumeIds: string[],
  currentStudyId: string,
): string[] {
  return residentVolumeIds.filter((id) => {
    if (!id.startsWith(VOLUME_ID_PREFIX)) return false;
    const study = volumeBaseFromId(id).split("_")[0];
    return study !== currentStudyId;
  });
}

/**
 * Pure LRU step: given the volume ids currently bound to slots and the prior
 * LRU (most-recent first, FULL ids), return the next LRU and the full volume
 * ids to evict (tail-first, oldest first) once over `cap`. Bound ids are kept
 * at the front and are never evicted (assuming bound.length <= cap, which holds
 * for the ≤4-slot grid).
 */
export function computeVolumeLru(args: {
  boundVolumeIds: string[];
  priorLru: string[];
  cap: number;
}): { nextLru: string[]; evicted: string[] } {
  const { boundVolumeIds, priorLru, cap } = args;
  const bound = new Set(boundVolumeIds.filter(Boolean));
  const next = Array.from(bound);
  for (const id of priorLru) {
    if (!bound.has(id) && !next.includes(id)) next.push(id);
  }
  const evicted: string[] = [];
  // Pop from the tail (oldest) while over cap, but never evict a currently-bound
  // (in-use) volume — bound ids sit at the front, so stop once the tail is one.
  while (next.length > cap && !bound.has(next[next.length - 1])) {
    const victim = next.pop();
    if (!victim) break;
    evicted.push(victim);
  }
  return { nextLru: next, evicted };
}
